import type { INovaWarehouse } from '@/interfaces/nova';
import type { useProgress } from '@/composables/progressBar';
import { isAxiosError } from 'axios';
import { isString } from 'lodash';
import { deleteProperty, getProperty } from '@/services/util';

export const ALREADY_EXISTS_MESSAGE = 'N/A (already exists in Nova)';
export const SUCCESS_IMPORT_MESSAGE = 'Success';

export type ImportProgress = Pick<
  ReturnType<typeof useProgress>,
  'createInterval' | 'zoom' | 'update' | 'clear' | 'setDialogVisibility'
>;

export interface IImportSession {
  notificationsMuteAttempted: boolean;
  warehouseReport: INovaWarehouse[];
  warehouseCustomFields: Record<string, INovaWarehouse['customApptFieldsTemplate']> | null;
}

export type failureStatusMessageParamsError = {
  e: unknown;
  message?: never;
};

export type failureStatusMessageParamsMessage = {
  e?: never;
  message: string;
};

export function getErrorMessage(error: unknown): string {
  if (isAxiosError(error)) {
    const responseMessage = (error.response?.data as { message?: unknown } | undefined)?.message;
    // ValidationPipe answers with one message per failed constraint.
    if (Array.isArray(responseMessage) && responseMessage.length > 0) {
      return responseMessage.join(', ');
    }
    if (isString(responseMessage)) {
      return responseMessage;
    }
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return 'An unknown error occurred';
}

export function getFailureStatusMessage({
  e,
  message
}: failureStatusMessageParamsError | failureStatusMessageParamsMessage) {
  return `FAIL REASON: ${message ?? getErrorMessage(e)}`;
}

export function wasAlreadyImported(entity: { wasImportedIntoNova?: unknown } | undefined) {
  return (
    entity?.wasImportedIntoNova === SUCCESS_IMPORT_MESSAGE ||
    entity?.wasImportedIntoNova === ALREADY_EXISTS_MESSAGE
  );
}

/** Runs the handler for each item one at a time, preserving order. */
export function forEachSequentially<T>(items: readonly T[], handler: (item: T) => Promise<void>) {
  return items.reduce<Promise<void>>(
    (chain, item) => chain.then(() => handler(item)),
    Promise.resolve()
  );
}

/** Sheet cells hold JSON text; once parsed during the import the same field holds the parsed value. */
export function parseJsonField<TParsed>(value: string | TParsed): TParsed {
  return isString(value) ? (JSON.parse(value) as TParsed) : value;
}

export function removeEmptyProperties(entity: object) {
  Object.keys(entity).forEach(key => {
    const propertyVal = getProperty(entity, key);
    if (typeof propertyVal !== 'boolean' && propertyVal !== 0 && !propertyVal) {
      deleteProperty(entity, key);
    }
  });
}
